'use client';

import { ColumnDef } from '@tanstack/react-table';
import { Tag, Ellipsis } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ResourceBadge } from '@/components/ui/resource-badge';
import { OfferItem } from '@/lib/api/offers';
import { DateDisplay } from '@/components/date-display';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { OfferResourceBadge } from '@/components/offer/OfferResourceBadge';
import { formatMoney } from "@/lib/money";

interface GetOffersTableColumnsProps {
    storeId: string;
    onEditOffer: (offerId: string) => void;
    onDeleteOffer: (offerId: string) => void;
}

export const useOffersTableColumns = ({
    storeId,
    onEditOffer,
    onDeleteOffer,
}: GetOffersTableColumnsProps): ColumnDef<OfferItem>[] => {
    const { t, i18n } = useTranslation('stores');
    return React.useMemo(() => [
        {
            id: 'select',
            header: ({ table }) => (
                <Checkbox
                    checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label={t('offersTable.selectAll')}
                    className="translate-y-0.5"
                />
            ),
            cell: ({ row }) => (
                <DataTableCheckbox row={row} />
            ),
            enableSorting: false,
            enableHiding: false,
            size: 40,
        },
        {
            id: 'id',
            accessorKey: 'id',
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('offersTable.columns.id')} />,
            cell: ({ row }) => {
                const offer = row.original;
                return (<OfferResourceBadge offerId={offer.id} />);
            },
            meta: { label: t('offersTable.columns.id') },
            enableSorting: true,
        },
        {
            id: 'name',
            accessorFn: (row) => row.titleTrans?.[i18n.language] || row.titleTrans?.en || '',
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('offersTable.columns.name')} />,
            cell: ({ row }) => {
                const offer = row.original;
                const { storeRoutes } = useStoreNavigate();
                const offerTitle = offer.titleTrans?.[i18n.language] || offer.titleTrans?.en || t('offersTable.untitledOffer');
                return (
                    <div className="min-w-0">
                        <Link to={storeRoutes.OFFER_EDIT(offer.id)}>
                            <div className="truncate font-medium hover:underline">{offerTitle}</div>
                        </Link>
                    </div>
                );
            },
            meta: {
                label: t('offersTable.columns.name'),
                placeholder: t('offersTable.searchOffers'),
                variant: 'text',
                icon: () => <Tag />,
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            id: 'type',
            accessorKey: 'type',
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('offersTable.columns.type')} />,
            cell: ({ row }) => {
                const offer = row.original;
                const typeKey = `offersTable.types.${offer.type}`;
                const translated = t(typeKey);
                return <Badge variant="outline">{translated === typeKey ? offer.type : translated}</Badge>;
            },
            enableSorting: true,
            meta: { label: t('offersTable.columns.type') },
        },

        {
            id: 'products',
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('offersTable.columns.products')} />,
            cell: ({ row }) => {
                const offer = row.original;
                const lineItems = offer.offerLineItems || [];
                if (lineItems.length === 0) {
                    return <span className="text-muted-foreground text-sm">{t('offersTable.noProducts')}</span>;
                }
                return (
                    <div className="flex flex-col gap-1">
                        {lineItems.map((item) => {
                            const productName = item.price?.variant?.product?.name || t('offersTable.unknownProduct');
                            const imageUrl = item.price?.variant?.imageUrl;
                            return (
                                <div key={item.id} className="flex items-center gap-2">
                                    {imageUrl && (
                                        <img
                                            src={imageUrl}
                                            alt={productName}
                                            className="h-8 w-8 rounded object-cover"
                                        />
                                    )}
                                    <span className="text-sm truncate">{productName}</span>
                                </div>
                            );
                        })}
                    </div>
                );
            },
            enableSorting: false,
            meta: { label: t('offersTable.columns.products') },
        },
        {
            id: 'prices',
            accessorKey: 'prices',
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('offersTable.columns.prices')} />,
            cell: ({ row }) => {
                const offer = row.original;
                const summaries = offer.summaries || [];
                if (summaries.length === 0) {
                    return <span className="text-muted-foreground text-sm">{t('offersTable.noPrices')}</span>;
                }
                return (
                    <div className="flex flex-col gap-1">
                        {summaries.map((summary, index) => {
                            const currency = summary.currency || 'USD';
                            const originalAmount = summary.totalAmount || 0;
                            const adjustedAmount = summary.totalAdjustedAmount || 0;
                            const hasDiscount = originalAmount !== adjustedAmount;
                            return (
                                <div key={index} className="text-sm">
                                    {hasDiscount ? (
                                        <div className="flex items-center gap-2">
                                            <span className="line-through text-muted-foreground text-gray-400">
                                                {formatMoney(originalAmount, currency)}
                                            </span>
                                            <span className="font-medium">
                                                {formatMoney(adjustedAmount, currency)}
                                            </span>
                                        </div>
                                    ) : (
                                        <span className="font-medium">
                                            {formatMoney(originalAmount, currency)}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            },
            enableSorting: false,
            meta: { label: t('offersTable.columns.prices') },
        },
        {
            id: 'promotions',
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('offersTable.columns.promotions')} />,
            cell: ({ row }) => {
                const offer = row.original;
                const promotionCount = offer.promotions?.length || 0;
                return (
                    <div className="text-sm">
                        <span className="font-medium">{promotionCount}</span>
                        <span className="text-muted-foreground ml-1">
                            {t('offersTable.promoCount', { count: promotionCount })}
                        </span>
                    </div>
                );
            },
            enableSorting: false,
            meta: { label: t('offersTable.columns.promotions') },
        },
        {
            id: 'expiryDate',
            accessorKey: 'expiryDate',
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('offersTable.columns.expiryDate')} />,
            cell: ({ row }) => {
                const offer = row.original;
                if (!offer.expiryDate) {
                    return <span className="text-muted-foreground">{t('offersTable.noExpiry')}</span>;
                }
                const isExpired = new Date(offer.expiryDate) < new Date();
                return (
                    <div className="text-sm">
                        <DateDisplay
                            value={offer.expiryDate}
                            mode="date"
                            className={isExpired ? 'text-red-600' : ''}
                        />
                        {isExpired && (
                            <Badge variant="destructive" className="mt-1">{t('offersTable.expired')}</Badge>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            meta: { label: t('offersTable.columns.expiryDate') },
        },
        {
            id: 'createdAt',
            accessorKey: 'createdAt',
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('offersTable.columns.created')} />,
            cell: ({ row }) => (
                <DateDisplay
                    value={row.original.createdAt}
                    mode="split"
                    className="text-sm font-medium tabular-nums"
                />
            ),
            enableSorting: true,
            meta: { label: t('offersTable.columns.created') },
        },
        {
            id: 'actions',
            cell: ({ row }) => {
                const offer = row.original;
                const { storeRoutes } = useStoreNavigate();
                return (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                aria-label={t('offersTable.openMenu')}
                                variant="ghost"
                                className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
                            >
                                <Ellipsis className="h-4 w-4" aria-hidden="true" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-[160px]">
                            <DropdownMenuItem onClick={() => onEditOffer(offer.id)}>
                                {t('offersTable.actions.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                                <Link to={storeRoutes.OFFER_EDIT(offer.id)}>{t('offersTable.actions.viewDetails')}</Link>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onClick={() => onDeleteOffer(offer.id)}
                                className="text-destructive focus:text-destructive"
                            >
                                {t('offersTable.actions.delete')}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            },
            enableSorting: false,
            enableHiding: false,
            size: 40,
        },
    ], [t, i18n.language, storeId, onEditOffer, onDeleteOffer]);
};
